import React, {
    useEffect,
    useMemo,
    useRef,
} from "react";

import {
    FiCheck,
    FiMinus,
    FiShield,
} from "react-icons/fi";

import "./RoleManagement.css";


/*
|--------------------------------------------------------------------------
| FORMAT LABEL
|--------------------------------------------------------------------------
*/

const formatEmployeeLabel = (
    value
) => {

    return String(
        value ||
        ""
    )
        .replace(
            /[-.]/g,
            "_"
        )
        .toLowerCase()
        .split(
            "_"
        )
        .filter(Boolean)
        .map(
            (
                word
            ) =>
                word
                    .charAt(0)
                    .toUpperCase() +
                word.slice(1)
        )
        .join(" ");

};


/*
|--------------------------------------------------------------------------
| OBJECT ID
|--------------------------------------------------------------------------
*/

const getObjectId = (
    value
) => {

    if (
        !value
    ) {

        return "";

    }


    if (
        typeof value ===
        "string"
    ) {

        return value;

    }


    return String(
        value?._id ||
        value?.id ||
        ""
    );

};


/*
|--------------------------------------------------------------------------
| PERMISSION FEATURE
|--------------------------------------------------------------------------
*/

const getPermissionFeature = (
    permission
) => {

    if (
        permission?.groupName
    ) {

        return String(
            permission.groupName
        )
            .trim();

    }


    const code =
        String(
            permission?.code ||
            ""
        )
            .trim()
            .toLowerCase();


    if (
        !code
    ) {

        return "General";

    }


    const parts =
        code
            .split(".")
            .filter(Boolean);


    if (
        parts.length <=
        2
    ) {

        /*
         * For normal two-part codes such as customer.view / task.view,
         * use the first code segment as the feature fallback.
         */
        return formatEmployeeLabel(
            parts[0] ||
            "General"
        );

    }


    return formatEmployeeLabel(
        parts
            .slice(
                1,
                -1
            )
            .join("_")
    );

};


/*
|--------------------------------------------------------------------------
| BUILD PERMISSION GROUPS
|--------------------------------------------------------------------------
*/

const buildPermissionGroups = (
    permissionModules = []
) => {

    if (
        !Array.isArray(
            permissionModules
        )
    ) {

        return [];

    }


    return permissionModules
        .map(
            (
                module
            ) => {

                const permissions =
                    Array.isArray(
                        module?.permissions
                    )
                        ?
                        module.permissions
                        :
                        [];


                const featureMap =
                    new Map();


                permissions
                    .forEach(
                        (
                            permission
                        ) => {

                            const featureName =
                                getPermissionFeature(
                                    permission
                                );


                            if (
                                !featureMap.has(
                                    featureName
                                )
                            ) {

                                featureMap.set(
                                    featureName,
                                    {
                                        name:
                                            featureName,

                                        permissions:
                                            [],
                                    }
                                );

                            }


                            featureMap
                                .get(
                                    featureName
                                )
                                .permissions
                                .push(
                                    permission
                                );

                        }
                    );


                return {

                    ...module,

                    code:
                        String(
                            module?.code ||
                            ""
                        )
                            .trim()
                            .toUpperCase(),

                    name:
                        module?.name ||
                        formatEmployeeLabel(
                            module?.code
                        ),

                    permissions,

                    features:
                        Array.from(
                            featureMap.values()
                        ),

                };

            }
        )
        .filter(
            (
                module
            ) =>
                module.permissions.length >
                0
        );

};


/*
|--------------------------------------------------------------------------
| ALL PERMISSION IDS
|--------------------------------------------------------------------------
*/

const getAllPermissionIds = (
    groups = []
) => {

    return (
        Array.isArray(
            groups
        )
            ?
            groups
            :
            []
    )
        .flatMap(
            (
                module
            ) =>
                (
                    module?.features ||
                    []
                )
                    .flatMap(
                        (
                            feature
                        ) =>
                            (
                                feature?.permissions ||
                                []
                            )
                                .map(
                                    getObjectId
                                )
                    )
        )
        .filter(Boolean);

};


/*
|--------------------------------------------------------------------------
| NORMALIZE ACTION
|--------------------------------------------------------------------------
*/

const normalizeAction = (
    value
) => {

    return String(
        value ||
        ""
    )
        .trim()
        .toUpperCase();

};


/*
|--------------------------------------------------------------------------
| ACTION LABEL
|--------------------------------------------------------------------------
*/

const getActionLabel = (
    action
) => {

    return formatEmployeeLabel(
        action
    );

};


/*
|--------------------------------------------------------------------------
| MATRIX CHECKBOX
|--------------------------------------------------------------------------
*/

const MatrixCheckbox = ({
    checked = false,

    indeterminate = false,

    disabled = false,

    onChange,

    ariaLabel = "",
}) => {

    const inputRef =
        useRef(
            null
        );


    /*
    |--------------------------------------------------------------------------
    | INDETERMINATE
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {

            if (
                inputRef.current
            ) {

                inputRef.current.indeterminate =
                    Boolean(
                        indeterminate
                    );

            }

        },
        [
            indeterminate,
        ]
    );


    return (

        <label
            className={[
                "employee-pm-checkbox",

                checked
                    ?
                    "checked"
                    :
                    "",

                indeterminate
                    ?
                    "indeterminate"
                    :
                    "",

                disabled
                    ?
                    "disabled"
                    :
                    "",
            ]
                .filter(
                    Boolean
                )
                .join(
                    " "
                )}
        >

            <input
                ref={
                    inputRef
                }

                type="checkbox"

                checked={
                    checked
                }

                disabled={
                    disabled
                }

                onChange={
                    onChange
                }

                aria-label={
                    ariaLabel
                }
            />


            <span>

                {
                    indeterminate
                        ?
                        <FiMinus />
                        :
                        checked
                            ?
                            <FiCheck />
                            :
                            null
                }

            </span>

        </label>

    );

};


/*
|--------------------------------------------------------------------------
| EMPLOYEE PERMISSION MATRIX
|--------------------------------------------------------------------------
*/

const RolePermissionMatrix = ({
    permissionModules = [],

    value = [],

    onChange,

    disabled = false,

    error = "",
}) => {

    /*
    |--------------------------------------------------------------------------
    | BUILD GROUPS FROM API
    |--------------------------------------------------------------------------
    */

    const groups =
        useMemo(
            () =>
                buildPermissionGroups(
                    permissionModules
                ),
            [
                permissionModules,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | ALL AVAILABLE PERMISSION IDS
    |--------------------------------------------------------------------------
    */

    const allPermissionIds =
        useMemo(
            () => {

                return getAllPermissionIds(
                    groups
                )
                    .map(
                        String
                    )
                    .filter(
                        Boolean
                    );

            },
            [
                groups,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | SELECTED IDS
    |--------------------------------------------------------------------------
    */

    const selectedIds =
        useMemo(
            () => {

                if (
                    !Array.isArray(
                        value
                    )
                ) {

                    return [];

                }


                return [
                    ...new Set(
                        value
                            .map(
                                String
                            )
                            .filter(
                                Boolean
                            )
                    ),
                ];

            },
            [
                value,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | SELECTED SET
    |--------------------------------------------------------------------------
    */

    const selectedSet =
        useMemo(
            () =>
                new Set(
                    selectedIds
                ),
            [
                selectedIds,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | SELECTED AVAILABLE COUNT
    |--------------------------------------------------------------------------
    */

    const selectedAvailableCount =
        useMemo(
            () => {

                return allPermissionIds
                    .filter(
                        (
                            id
                        ) =>
                            selectedSet.has(
                                id
                            )
                    )
                    .length;

            },
            [
                allPermissionIds,
                selectedSet,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | GLOBAL SELECT STATE
    |--------------------------------------------------------------------------
    */

    const allSelected =
        allPermissionIds.length >
        0 &&
        selectedAvailableCount ===
        allPermissionIds.length;


    const partiallySelected =
        selectedAvailableCount >
        0 &&
        !allSelected;


    /*
    |--------------------------------------------------------------------------
    | EMIT CHANGE
    |--------------------------------------------------------------------------
    */

    const emitChange = (
        ids
    ) => {

        if (
            disabled
        ) {

            return;

        }


        onChange?.(
            [
                ...new Set(
                    (
                        ids ||
                        []
                    )
                        .map(
                            String
                        )
                        .filter(
                            Boolean
                        )
                ),
            ]
        );

    };


    /*
    |--------------------------------------------------------------------------
    | TOGGLE SINGLE PERMISSION
    |--------------------------------------------------------------------------
    */

    const togglePermission = (
        permissionId
    ) => {

        const id =
            String(
                permissionId ||
                ""
            );


        if (
            !id
        ) {

            return;

        }


        if (
            selectedSet.has(
                id
            )
        ) {

            emitChange(
                selectedIds
                    .filter(
                        (
                            item
                        ) =>
                            item !==
                            id
                    )
            );


            return;

        }


        emitChange([
            ...selectedIds,
            id,
        ]);

    };


    /*
    |--------------------------------------------------------------------------
    | TOGGLE MULTIPLE IDS
    |--------------------------------------------------------------------------
    */

    const toggleIds = (
        ids
    ) => {

        const normalizedIds =
            [
                ...new Set(
                    (
                        ids ||
                        []
                    )
                        .map(
                            String
                        )
                        .filter(
                            Boolean
                        )
                ),
            ];


        if (
            normalizedIds.length ===
            0
        ) {

            return;

        }


        const everySelected =
            normalizedIds.every(
                (
                    id
                ) =>
                    selectedSet.has(
                        id
                    )
            );


        /*
        |--------------------------------------------------------------------------
        | REMOVE ALL
        |--------------------------------------------------------------------------
        */

        if (
            everySelected
        ) {

            const removeSet =
                new Set(
                    normalizedIds
                );


            emitChange(
                selectedIds
                    .filter(
                        (
                            id
                        ) =>
                            !removeSet.has(
                                id
                            )
                    )
            );


            return;

        }


        /*
        |--------------------------------------------------------------------------
        | ADD ALL
        |--------------------------------------------------------------------------
        */

        emitChange([
            ...selectedIds,
            ...normalizedIds,
        ]);

    };


    /*
    |--------------------------------------------------------------------------
    | GLOBAL SELECT ALL
    |--------------------------------------------------------------------------
    */

    const toggleAll = () => {

        /*
        |--------------------------------------------------------------------------
        | CLEAR ALL CURRENTLY AVAILABLE PERMISSIONS
        |--------------------------------------------------------------------------
        */

        if (
            allSelected
        ) {

            const availableSet =
                new Set(
                    allPermissionIds
                );


            emitChange(
                selectedIds
                    .filter(
                        (
                            id
                        ) =>
                            !availableSet.has(
                                id
                            )
                    )
            );


            return;

        }


        /*
        |--------------------------------------------------------------------------
        | SELECT ALL
        |--------------------------------------------------------------------------
        */

        emitChange([
            ...selectedIds,
            ...allPermissionIds,
        ]);

    };


    /*
    |--------------------------------------------------------------------------
    | MODULE PERMISSION IDS
    |--------------------------------------------------------------------------
    */

    const getModuleIds = (
        module
    ) => {

        return (
            module?.features ||
            []
        )
            .flatMap(
                (
                    feature
                ) =>
                    (
                        feature?.permissions ||
                        []
                    )
                        .map(
                            getObjectId
                        )
            )
            .filter(
                Boolean
            )
            .map(
                String
            );

    };


    /*
    |--------------------------------------------------------------------------
    | FEATURE PERMISSION IDS
    |--------------------------------------------------------------------------
    */

    const getFeatureIds = (
        feature
    ) => {

        return (
            feature?.permissions ||
            []
        )
            .map(
                getObjectId
            )
            .filter(
                Boolean
            )
            .map(
                String
            );

    };


    /*
    |--------------------------------------------------------------------------
    | MODULE ACTION COLUMNS
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |
    | Every module gets ONLY its own actions.
    |
    | Example:
    |
    | EMPLOYEE
    |
    | READ
    | CREATE
    | UPDATE
    | STATUS
    | LOGIN_ACCESS
    |
    | SALES & BILLING
    |
    | READ
    | CREATE
    | UPDATE
    | DELETE
    | PRINT
    |
    | Employee-only actions will NOT appear in Sales & Billing.
    |
    | Nothing is hardcoded.
    |
    |--------------------------------------------------------------------------
    */

    const getModuleActionColumns = (
        module
    ) => {

        const actions =
            [];


        const seen =
            new Set();


        (
            module?.features ||
            []
        )
            .forEach(
                (
                    feature
                ) => {

                    (
                        feature?.permissions ||
                        []
                    )
                        .forEach(
                            (
                                permission
                            ) => {

                                const action =
                                    normalizeAction(
                                        permission?.action
                                    );


                                if (
                                    !action ||
                                    seen.has(
                                        action
                                    )
                                ) {

                                    return;

                                }


                                seen.add(
                                    action
                                );


                                actions.push(
                                    action
                                );

                            }
                        );

                }
            );


        return actions;

    };


    /*
    |--------------------------------------------------------------------------
    | GET PERMISSION FOR FEATURE + ACTION
    |--------------------------------------------------------------------------
    */

    const getPermissionForAction = (
        feature,
        action
    ) => {

        return (
            feature?.permissions ||
            []
        )
            .find(
                (
                    permission
                ) => {

                    return (
                        normalizeAction(
                            permission?.action
                        ) ===
                        action
                    );

                }
            ) ||
            null;

    };


    /*
    |--------------------------------------------------------------------------
    | EMPTY STATE
    |--------------------------------------------------------------------------
    */

    if (
        groups.length ===
        0
    ) {

        return (

            <div className="employee-permission-empty">

                <FiShield />


                <strong>
                    No Permissions Available
                </strong>


                <span>
                    No permissions are available under the current subscription.
                </span>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (

        <div className="employee-pm">


            {/* ================================================================
                MAIN HEADER
            ================================================================ */}

            <div className="employee-pm-header">

                <div className="employee-pm-header-left">

                    <span className="employee-pm-header-icon">

                        <FiShield />

                    </span>


                    <div>

                        <strong>
                            Role Permissions
                        </strong>


                        <p>
                            Select exactly what this role can access.
                        </p>

                    </div>

                </div>


                <div className="employee-pm-header-right">

                    <span className="employee-pm-counter">

                        <b>

                            {
                                selectedAvailableCount
                            }

                        </b>

                        {" / "}

                        {
                            allPermissionIds.length
                        }

                        {" Selected"}

                    </span>


                    <label className="employee-pm-global-select">

                        <MatrixCheckbox
                            checked={
                                allSelected
                            }

                            indeterminate={
                                partiallySelected
                            }

                            disabled={
                                disabled
                            }

                            onChange={
                                toggleAll
                            }

                            ariaLabel="Select all permissions"
                        />


                        <strong>
                            Select All Permissions
                        </strong>

                    </label>

                </div>

            </div>


            {/* ================================================================
                ERROR
            ================================================================ */}

            {
                error
                    ?
                    (
                        <div className="employee-pm-error">

                            <small className="employee-common-error">

                                {
                                    error
                                }

                            </small>

                        </div>
                    )
                    :
                    null
            }


            {/* ================================================================
                MODULES
            ================================================================ */}

            <div className="employee-pm-modules">

                {
                    groups.map(
                        (
                            module,
                            moduleIndex
                        ) => {

                            /*
                            |--------------------------------------------------------------------------
                            | MODULE CODE
                            |--------------------------------------------------------------------------
                            */

                            const moduleCode =
                                String(
                                    module?.code ||
                                    module?.name ||
                                    `MODULE_${moduleIndex}`
                                );


                            /*
                            |--------------------------------------------------------------------------
                            | MODULE IDS
                            |--------------------------------------------------------------------------
                            */

                            const moduleIds =
                                getModuleIds(
                                    module
                                );


                            /*
                            |--------------------------------------------------------------------------
                            | MODULE ACTIONS ONLY
                            |--------------------------------------------------------------------------
                            */

                            const moduleActionColumns =
                                getModuleActionColumns(
                                    module
                                );


                            /*
                            |--------------------------------------------------------------------------
                            | MODULE SELECTION
                            |--------------------------------------------------------------------------
                            */

                            const moduleSelectedCount =
                                moduleIds
                                    .filter(
                                        (
                                            id
                                        ) =>
                                            selectedSet.has(
                                                id
                                            )
                                    )
                                    .length;


                            const moduleAllSelected =
                                moduleIds.length >
                                    0 &&
                                moduleSelectedCount ===
                                    moduleIds.length;


                            const modulePartial =
                                moduleSelectedCount >
                                    0 &&
                                !moduleAllSelected;


                            return (

                                <section
                                    key={
                                        moduleCode
                                    }

                                    className="employee-pm-module"
                                >


                                    {/* =================================================
                                        MODULE HEADER
                                    ================================================= */}

                                    <div className="employee-pm-module-header">

                                        <div className="employee-pm-module-title">

                                            <span>

                                                <FiShield />

                                            </span>


                                            <div>

                                                <h4>

                                                    {
                                                        module?.name ||
                                                        formatEmployeeLabel(
                                                            moduleCode
                                                        )
                                                    }

                                                </h4>


                                                <p>

                                                    {
                                                        moduleSelectedCount
                                                    }

                                                    {" of "}

                                                    {
                                                        moduleIds.length
                                                    }

                                                    {" permissions selected"}

                                                </p>

                                            </div>

                                        </div>


                                        <label className="employee-pm-module-select">

                                            <MatrixCheckbox
                                                checked={
                                                    moduleAllSelected
                                                }

                                                indeterminate={
                                                    modulePartial
                                                }

                                                disabled={
                                                    disabled
                                                }

                                                onChange={
                                                    () =>
                                                        toggleIds(
                                                            moduleIds
                                                        )
                                                }

                                                ariaLabel={
                                                    `Select all ${
                                                        module?.name ||
                                                        moduleCode
                                                    } permissions`
                                                }
                                            />


                                            <strong>
                                                Select All
                                            </strong>

                                        </label>

                                    </div>


                                    {/* =================================================
                                        TABLE
                                    ================================================= */}

                                    <div className="employee-pm-table-scroll">

                                        <table className="employee-pm-table">


                                            {/* =============================================
                                                MODULE-SPECIFIC HEADER
                                            ============================================= */}

                                            <thead>

                                                <tr>

                                                    <th className="employee-pm-feature-heading">

                                                        Feature

                                                    </th>


                                                    {
                                                        moduleActionColumns
                                                            .map(
                                                                (
                                                                    action
                                                                ) => (

                                                                    <th
                                                                        key={
                                                                            action
                                                                        }
                                                                    >

                                                                        {
                                                                            getActionLabel(
                                                                                action
                                                                            )
                                                                        }

                                                                    </th>

                                                                )
                                                            )
                                                    }


                                                    <th className="employee-pm-all-heading">

                                                        All

                                                    </th>

                                                </tr>

                                            </thead>


                                            {/* =============================================
                                                BODY
                                            ============================================= */}

                                            <tbody>

                                                {
                                                    (
                                                        module?.features ||
                                                        []
                                                    )
                                                        .map(
                                                            (
                                                                feature,
                                                                featureIndex
                                                            ) => {

                                                                /*
                                                                |--------------------------------------------------------------------------
                                                                | FEATURE IDS
                                                                |--------------------------------------------------------------------------
                                                                */

                                                                const featureIds =
                                                                    getFeatureIds(
                                                                        feature
                                                                    );


                                                                /*
                                                                |--------------------------------------------------------------------------
                                                                | FEATURE SELECTION
                                                                |--------------------------------------------------------------------------
                                                                */

                                                                const featureSelectedCount =
                                                                    featureIds
                                                                        .filter(
                                                                            (
                                                                                id
                                                                            ) =>
                                                                                selectedSet.has(
                                                                                    id
                                                                                )
                                                                        )
                                                                        .length;


                                                                const featureAllSelected =
                                                                    featureIds.length >
                                                                        0 &&
                                                                    featureSelectedCount ===
                                                                        featureIds.length;


                                                                const featurePartial =
                                                                    featureSelectedCount >
                                                                        0 &&
                                                                    !featureAllSelected;


                                                                return (

                                                                    <tr
                                                                        key={
                                                                            `${moduleCode}-${
                                                                                feature?.name ||
                                                                                featureIndex
                                                                            }`
                                                                        }
                                                                    >


                                                                        {/* ==================================
                                                                            FEATURE
                                                                        ================================== */}

                                                                        <td className="employee-pm-feature">

                                                                            <strong>

                                                                                {
                                                                                    feature?.name ||
                                                                                    "General"
                                                                                }

                                                                            </strong>


                                                                            <small>

                                                                                {
                                                                                    featureSelectedCount
                                                                                }

                                                                                {" / "}

                                                                                {
                                                                                    featureIds.length
                                                                                }

                                                                                {" selected"}

                                                                            </small>

                                                                        </td>


                                                                        {/* ==================================
                                                                            ACTIONS FOR THIS MODULE ONLY
                                                                        ================================== */}

                                                                        {
                                                                            moduleActionColumns
                                                                                .map(
                                                                                    (
                                                                                        action
                                                                                    ) => {

                                                                                        const permission =
                                                                                            getPermissionForAction(
                                                                                                feature,
                                                                                                action
                                                                                            );


                                                                                        /*
                                                                                        |--------------------------------------------------------------------------
                                                                                        | FEATURE DOES NOT HAVE THIS ACTION
                                                                                        |--------------------------------------------------------------------------
                                                                                        |
                                                                                        | Keep — only because the action exists somewhere inside
                                                                                        | this same module.
                                                                                        |
                                                                                        |--------------------------------------------------------------------------
                                                                                        */

                                                                                        if (
                                                                                            !permission
                                                                                        ) {

                                                                                            return (

                                                                                                <td
                                                                                                    key={
                                                                                                        action
                                                                                                    }

                                                                                                    className="employee-pm-empty-action"
                                                                                                >

                                                                                                    —

                                                                                                </td>

                                                                                            );

                                                                                        }


                                                                                        /*
                                                                                        |--------------------------------------------------------------------------
                                                                                        | PERMISSION ID
                                                                                        |--------------------------------------------------------------------------
                                                                                        */

                                                                                        const permissionId =
                                                                                            String(
                                                                                                getObjectId(
                                                                                                    permission
                                                                                                ) ||
                                                                                                ""
                                                                                            );


                                                                                        /*
                                                                                        |--------------------------------------------------------------------------
                                                                                        | CHECKED
                                                                                        |--------------------------------------------------------------------------
                                                                                        */

                                                                                        const checked =
                                                                                            Boolean(
                                                                                                permissionId
                                                                                            ) &&
                                                                                            selectedSet.has(
                                                                                                permissionId
                                                                                            );


                                                                                        return (

                                                                                            <td
                                                                                                key={
                                                                                                    action
                                                                                                }

                                                                                                className={
                                                                                                    checked
                                                                                                        ?
                                                                                                        "employee-pm-action selected"
                                                                                                        :
                                                                                                        "employee-pm-action"
                                                                                                }
                                                                                            >

                                                                                                <MatrixCheckbox
                                                                                                    checked={
                                                                                                        checked
                                                                                                    }

                                                                                                    disabled={
                                                                                                        disabled ||
                                                                                                        !permissionId
                                                                                                    }

                                                                                                    onChange={
                                                                                                        () =>
                                                                                                            togglePermission(
                                                                                                                permissionId
                                                                                                            )
                                                                                                    }

                                                                                                    ariaLabel={
                                                                                                        `${
                                                                                                            feature?.name ||
                                                                                                            "Feature"
                                                                                                        } ${
                                                                                                            getActionLabel(
                                                                                                                action
                                                                                                            )
                                                                                                        }`
                                                                                                    }
                                                                                                />

                                                                                            </td>

                                                                                        );

                                                                                    }
                                                                                )
                                                                        }


                                                                        {/* ==================================
                                                                            FEATURE SELECT ALL
                                                                        ================================== */}

                                                                        <td className="employee-pm-action employee-pm-row-all">

                                                                            <MatrixCheckbox
                                                                                checked={
                                                                                    featureAllSelected
                                                                                }

                                                                                indeterminate={
                                                                                    featurePartial
                                                                                }

                                                                                disabled={
                                                                                    disabled ||
                                                                                    featureIds.length ===
                                                                                        0
                                                                                }

                                                                                onChange={
                                                                                    () =>
                                                                                        toggleIds(
                                                                                            featureIds
                                                                                        )
                                                                                }

                                                                                ariaLabel={
                                                                                    `Select all ${
                                                                                        feature?.name ||
                                                                                        "feature"
                                                                                    } permissions`
                                                                                }
                                                                            />

                                                                        </td>

                                                                    </tr>

                                                                );

                                                            }
                                                        )
                                                }

                                            </tbody>

                                        </table>

                                    </div>

                                </section>

                            );

                        }
                    )
                }

            </div>


            {/* ================================================================
                FOOTER
            ================================================================ */}

            <div className="employee-pm-footer">

                <div>

                    <FiShield />


                    <strong>

                        Only permissions available in the current subscription are shown.

                    </strong>

                </div>


                <strong>

                    {
                        selectedAvailableCount
                    }

                    {" of "}

                    {
                        allPermissionIds.length
                    }

                    {" permissions selected"}

                </strong>

            </div>

        </div>

    );

};


export default RolePermissionMatrix;